import { Globe, Users, Lock, Save, Loader2, AlertCircle } from "lucide-react";
import { cn } from "../../lib/utils";
import type { MapSaveSheetProps, MapWorkspaceShareScope } from "../../contracts/map-workspace";
import { Input } from "../../internal/primitives/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../internal/primitives/select";
import { Label } from "../../internal/primitives/label";

const ShareScopeIcon = ({ scope }: { scope?: MapWorkspaceShareScope | null }) => {
  switch (scope) {
    case "public":
      return <Globe className="h-4 w-4" />;
    case "shared":
      return <Users className="h-4 w-4" />;
    case "tenant":
      return <Lock className="h-4 w-4" />;
    default:
      return null;
  }
}

export function MapSaveSheet({
  assetKind,
  title,
  description,
  name,
  summary,
  details,
  shareScope,
  shareScopeOptions,
  submitting,
  errorMessage,
  onNameChange,
  onDescriptionChange,
  onShareScopeChange,
  onCancel,
  onSubmit,
  className,
}: MapSaveSheetProps) {
  return (
    <div className={cn("flex flex-col h-full bg-background text-foreground", className)}>
      <div className="flex-1 overflow-y-auto p-6 space-y-8">
        <header className="space-y-1">
          <h2 className="text-xl font-bold tracking-tight">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </header>

        <div className="space-y-6">
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="asset-name" className="text-sm font-semibold">
              Name
            </Label>
            <Input
              id="asset-name"
              placeholder={`Enter ${assetKind === "style" ? "style" : "map"} name...`}
              value={name}
              onChange={(event) => onNameChange(event.target.value)}
              disabled={submitting}
            />
          </div>

          {/* Description Field */}
          <div className="space-y-2">
            <Label htmlFor="asset-description" className="text-sm font-semibold">
              Description
            </Label>
            <textarea
              id="asset-description"
              className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              placeholder={`Briefly describe this ${assetKind === "style" ? "style" : "map"}...`}
              value={description}
              onChange={(event) => onDescriptionChange(event.target.value)}
              disabled={submitting}
            />
          </div>

          {/* Share Scope Field */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Visibility</Label>
            <Select
              value={shareScope}
              onValueChange={(value) => onShareScopeChange(value as MapWorkspaceShareScope)}
              disabled={submitting}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select visibility" />
              </SelectTrigger>
              <SelectContent>
                {shareScopeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                    <div className="flex items-center gap-2">
                      <ShareScopeIcon scope={option.value} />
                      <span>{option.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground italic">
              Controls who can discover and use this {assetKind === "style" ? "style" : "map"} in other workspaces.
            </p>
          </div>
        </div>

        {/* Summary/Details read-only info if provided */}
        {(summary || details) && (
          <div className="rounded-lg bg-muted/30 p-4 border space-y-2">
            <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Asset Summary</h4>
            <div className="space-y-3">
              {summary && <p className="text-xs text-muted-foreground leading-relaxed">{summary}</p>}
              {details && <p className="text-xs font-mono bg-background/50 p-2 rounded border">{details}</p>}
            </div>
          </div>
        )}

        {/* Error Messaging */}
        {errorMessage && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <p>{errorMessage}</p>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t bg-muted/10 flex items-center justify-end gap-3">
        <button
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium rounded-md hover:bg-muted transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onSubmit}
          disabled={submitting || !name.trim()}
          className="flex items-center gap-2 px-6 py-2 text-sm font-semibold rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save {assetKind === "style" ? "Style" : "Map"}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
